import React, { FC, useState } from 'react';
import { Button, Input, SideModal, Typography } from 'design_system/src';
import { ReactComponent as Attachment } from 'design_system/src/static/icons/attachment-rotate.svg';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-sm.svg';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import {
	createWhitelistBatchFromFile,
	createWhitelistEntry,
} from 'models/whitelist/controllers/whitelist.controller';
import {
	WhitelistNameEnum,
	WhitelistNameValues,
	WhitelistTypeEnum,
	WhitelistTypeValues,
} from 'models/whitelist/interfaces/whitelist.interface';
import {
	getRequiredTypeForName,
	isValidWhitelistValue,
	normalizeWhitelistValue,
} from 'models/whitelist/helpers/whitelist-value.helpers';

const typeOptions = Object.values(WhitelistTypeEnum).map((value) => ({
	value,
	label: WhitelistTypeValues[value],
}));

const nameOptions = Object.values(WhitelistNameEnum).map((value) => ({
	value,
	label: WhitelistNameValues[value],
}));

type UploadMode = 'single' | 'batch';

interface CreateWhitelistModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	onCreated: () => void;
}

const CreateWhitelistModal: FC<CreateWhitelistModalProps> = ({
	open,
	setOpen,
	onCreated,
}) => {
	const [mode, setMode] = useState<UploadMode>('single');
	const [name, setName] = useState<WhitelistNameEnum>(
		WhitelistNameEnum.abandonedCart,
	);
	const [type, setType] = useState<WhitelistTypeEnum>(WhitelistTypeEnum.curp);
	const [value, setValue] = useState('');
	const [file, setFile] = useState<File | null>(null);
	const [expiresAt, setExpiresAt] = useState('');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const reset = () => {
		setMode('single');
		setName(WhitelistNameEnum.abandonedCart);
		setType(WhitelistTypeEnum.curp);
		setValue('');
		setFile(null);
		setExpiresAt('');
		setError('');
	};

	const handleClose = () => {
		reset();
		setOpen(false);
	};

	const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFile(e.target.files?.[0] ?? null);
	};

	const handleNameChange = (newName: WhitelistNameEnum) => {
		setName(newName);
		const requiredType = getRequiredTypeForName(newName);
		if (requiredType) setType(requiredType);
	};

	const handleSubmit = () => {
		setError('');

		if (mode === 'single') {
			const normalizedValue = normalizeWhitelistValue(type, value);
			if (!isValidWhitelistValue(type, normalizedValue)) {
				setError(
					type === WhitelistTypeEnum.curp
						? 'El valor debe ser una CURP válida'
						: 'El valor no debe estar vacío',
				);
				return;
			}

			setSaving(true);
			createWhitelistEntry(name, type, normalizedValue, expiresAt)
				.then(() => {
					toasterNotification({
						msg: 'Valor guardado exitosamente',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					onCreated();
					handleClose();
				})
				.catch(commonErrorHandler)
				.finally(() => setSaving(false));
			return;
		}

		if (!file) {
			setError('Subí un archivo .xlsx o .json');
			return;
		}

		setSaving(true);
		createWhitelistBatchFromFile(name, type, file, expiresAt)
			.then((result) => {
				toasterNotification({
					msg: `Lote procesado: ${result.created} nuevos, ${result.updated} actualizados`,
					style: 'light',
					toasterType: toasterOptions.success,
				});
				onCreated();
				handleClose();
			})
			.catch(commonErrorHandler)
			.finally(() => setSaving(false));
	};

	return (
		<SideModal open={open} setOpen={handleClose} size="small">
			<div className="flex_content_col">
				<Typography
					scale="heading4"
					weight="600"
					textColor="primary_300"
					className="text_center flex_header p_b_md"
				>
					Agregar a la lista blanca
				</Typography>
				<div className="flex_body p_x_md">
					<div className="display_flex flex_gap_sm m_b_md">
						<Button
							scale="small"
							variant={mode === 'single' ? 'principal' : 'outline'}
							onClick={() => setMode('single')}
						>
							Uno por uno
						</Button>
						<Button
							scale="small"
							variant={mode === 'batch' ? 'principal' : 'outline'}
							onClick={() => setMode('batch')}
						>
							Por lotes
						</Button>
					</div>

					<Input
						title="Nombre de la lista blanca"
						name="name"
						type="select"
						className="m_b_md w_100_per"
						options={nameOptions}
						value={name}
						onChange={(e: any) => handleNameChange(e.target.value)}
					/>

					<Input
						title="Tipo"
						name="type"
						type="select"
						className="m_b_md w_100_per"
						options={typeOptions}
						value={type}
						disabled={!!getRequiredTypeForName(name)}
						onChange={(e: any) => setType(e.target.value)}
					/>

					{mode === 'single' ? (
						<Input
							title="Valor"
							name="value"
							placeholder={
								type === WhitelistTypeEnum.curp
									? 'Ejemplo: HENN890508MDFRVD03'
									: 'Ejemplo: PJ-DACA47'
							}
							type="text"
							className="m_b_md w_100_per"
							value={value}
							onChange={(e: any) => setValue(e.target.value)}
						/>
					) : (
						<div className="dso_input_cont m_b_md w_100_per">
							<div className="dso_input_title">
								<div className="dso_input_text">Archivo (.xlsx o .json)</div>
							</div>
							<label
								className="dso_select display_flex flex_justify_between pos_relative"
								htmlFor="batch-file"
							>
								<input
									id="batch-file"
									type="file"
									accept=".xlsx,.xls,.json"
									onChange={handleFile}
									className="input-file"
								/>
								<div className="display_flex flex_gap_md">
									<Attachment
										className={`dim_lg border_transparent ${
											file ? 'text_primary_300' : 'text_neutral_600'
										}`}
									/>
									{file ? (
										<Typography
											scale="small"
											textColor="primary_300"
											weight="600"
										>
											{file.name}
										</Typography>
									) : (
										<Typography
											scale="small"
											textColor="neutral_600"
											weight="600"
										>
											Seleccione un archivo
										</Typography>
									)}
								</div>
								{file ? (
									<Close
										className="dim_lg text_primary_300 pos_absolute z-indez"
										style={{ right: 10 }}
										role="button"
										tabIndex={-1}
										onClick={(e) => {
											e.stopPropagation();
											setFile(null);
										}}
										onKeyDown={(e) => {
											e.stopPropagation();
											setFile(null);
										}}
									/>
								) : null}
							</label>
						</div>
					)}

					<div className="display_flex flex_gap_sm flex_align_center m_b_md w_100_per">
						<Input
							title="Fecha de expiración (opcional)"
							name="expiresAt"
							type="date"
							className="flex_grow_1"
							value={expiresAt}
							onChange={(e: any) => setExpiresAt(e.target.value)}
							subtitle="Si la dejás vacía, el valor no expira"
						/>
						{expiresAt && (
							<Button
								scale="small"
								variant="ghost"
								color="red"
								onClick={() => setExpiresAt('')}
							>
								Quitar
							</Button>
						)}
					</div>

					{error && (
						<Typography
							scale="small"
							weight="400"
							textColor="red_300"
							className="m_b_md"
						>
							{error}
						</Typography>
					)}

					<Button
						scale="small"
						variant="principal"
						className="w_100_per"
						disabled={saving}
						onClick={handleSubmit}
					>
						{saving ? 'Guardando...' : 'Guardar'}
					</Button>
				</div>
			</div>
		</SideModal>
	);
};

export default CreateWhitelistModal;
